import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatPriceAUD, formatUnits, formatPct } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { assetClassLabel } from '../../../shared/portfolio';
import { topExposures, largestDetractors, type ExposureRow } from '../../../shared/exposures';
import { QAChartTooltip } from './QAChartTooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { PortfolioSnapshot } from '../../../shared/api';
import { MacquarieUploadDialog } from './MacquarieUploadDialog';

const QA_COLOURS = ['#B8431A', '#A07A3F', '#2F4636', '#8A6418', '#7A6E63', '#3D3530'];

export function PortfolioTab({ portfolio }: { portfolio: PortfolioSnapshot }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [tradeModeByAccount, setTradeModeByAccount] = useState<Record<string, boolean>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  });

  if (!portfolio || !portfolio.accounts || portfolio.accounts.length === 0) {
    return (
      <div className="mt-6">
        <MacquarieUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} context="portfolio" />
        <div className="flex justify-end mb-4">
          <button onClick={() => setUploadOpen(true)} className="qa-btn-secondary text-sm">Upload Macquarie Report</button>
        </div>
        <QAEmpty inline title="No accounts yet." />
      </div>
    );
  }

  const flatHoldings = portfolio.accounts.flatMap((a) => a.holdings);
  const allocationLabelled = portfolio.allocation.map((sl) => ({ ...sl, bucket: assetClassLabel(sl.bucket) }));
  const exposures = topExposures(flatHoldings, portfolio.tnw, 5);
  const detractors = largestDetractors(flatHoldings, portfolio.tnw, 5);

  return (
    <div className="mt-6">
      <MacquarieUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} context="portfolio" />
      {(exposures.length > 0 || detractors.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <ExposureCard title="Top exposures" rows={exposures} kind="exposure" />
          <ExposureCard title="Largest detractors" rows={detractors} kind="detractor" />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-2 qa-card p-6">
          <p className="qa-eyebrow mb-3">Asset allocation</p>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={allocationLabelled} dataKey="value" nameKey="bucket" innerRadius={60} outerRadius={100} stroke="#FBF7EF">
                  {allocationLabelled.map((_, i: number) => (
                    <Cell key={i} fill={QA_COLOURS[i % QA_COLOURS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<QAChartTooltip valueFormat={(v, entry) => `${formatAUD(Number(v))}${entry.payload?.pct != null ? ` · ${formatPct(entry.payload.pct as number)}` : ''}`} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="qa-card p-6">
          <div className="flex items-baseline justify-between mb-3">
            <p className="qa-eyebrow">Refresh prices</p>
            <button onClick={() => refresh.mutate()} className="btn-ghost text-sm" disabled={refresh.isPending}>
              {refresh.isPending ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <div className="flex items-baseline justify-between mb-3 mt-4 pt-4 border-t border-paper-trim">
            <p className="qa-eyebrow">Upload Report</p>
            <button onClick={() => setUploadOpen(true)} className="btn-ghost text-sm">
              Macquarie XLS
            </button>
          </div>
          <p className="text-xs text-ink-mute mb-3">Mock provider. Real IRESS adapter requires credentials.</p>
          <ul className="space-y-1 text-sm">
            {portfolio.allocation.map((slice, i: number) => (
              <li key={slice.bucket} className="flex justify-between qa-mono">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-2 h-2" style={{ background: QA_COLOURS[i % QA_COLOURS.length] }} />
                  {assetClassLabel(slice.bucket)}
                </span>
                <span>{formatPct(slice.pct)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {portfolio.accounts.map((a) => {
        const isOpen = open[a.id] !== false; // open by default
        const tradeMode = tradeModeByAccount[a.id] ?? false;
        return (
          <section key={a.id} className="qa-card mb-4">
            <header className="px-5 py-3 flex items-center justify-between">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(o => ({ ...o, [a.id]: !isOpen }))}
                className="flex-1 text-left"
              >
                <p className="qa-mono text-xs text-ink-mute uppercase tracking-widest">{a.type}</p>
                <h3 className="qa-display text-lg">{a.name}</h3>
              </button>
              <div className="flex items-center gap-6">
                <button
                  onClick={(e) => { e.stopPropagation(); setTradeModeByAccount((m) => ({ ...m, [a.id]: !tradeMode })); }}
                  className={`btn-ghost text-sm ${tradeMode ? 'text-forest' : ''}`}
                  title="Opens an external IRESS order ticket; nothing is placed automatically"
                >
                  {tradeMode ? 'Exit Trades' : 'Enter Trades'}
                </button>
                <div className="text-right">
                  <p className="qa-eyebrow">Account value</p>
                  <p className="qa-mono text-lg">{formatAUD(a.value)}</p>
                  <p className="text-xs text-ink-mute">{formatAUD(a.cashBalance)} cash</p>
                </div>
              </div>
            </header>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="qa-table">
                  <thead>
                    <tr>
                      <th>Security</th><th>Class</th>
                      <th className="text-right">Units</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Yield</th>
                      <th className="text-right">P/E</th>
                      <th className="text-right">1Y Perf</th>
                      <th className="text-right">Value</th>
                      <th className="text-right">U/G/L</th>
                      <th className="text-right">Return</th>
                      {tradeMode && <th className="text-right">Trade</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {a.holdings.map((h) => (
                      <tr key={h.code}>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{h.code}</span>
                            <span className="text-ink-mute truncate max-w-[150px]" title={h.name}>{h.name}</span>
                            {h.marketCap && (
                              <span className="text-[10px] text-ink-mute px-1 bg-ink/5 rounded" title="Market Cap">
                                {(h.marketCap / 1e9).toFixed(1)}B
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="text-xs text-ink-mute">{assetClassLabel(h.assetClass)}</td>
                        <td className="num">{formatUnits(h.units)}</td>
                        <td className="num">{formatPriceAUD(h.currentPrice)}</td>
                        <td className="num">{h.yieldPct !== undefined ? formatPct(h.yieldPct) : '-'}</td>
                        <td className="num">{h.peRatio !== undefined ? h.peRatio.toFixed(1) : '-'}</td>
                        <td className={`num ${h.performance1Y !== undefined ? (h.performance1Y >= 0 ? 'text-forest' : 'text-rust-deep') : ''}`}>
                          {h.performance1Y !== undefined ? `${h.performance1Y >= 0 ? '+' : ''}${formatPct(h.performance1Y)}` : '-'}
                        </td>
                        <td className="num">{formatAUD(h.currentValue)}</td>
                        <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                          {h.unrealisedGainLoss >= 0 ? '+' : ''}{formatAUD(h.unrealisedGainLoss)}
                        </td>
                        <td className={`num ${h.unrealisedGainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}`}>
                          {h.unrealisedReturnPct >= 0 ? '+' : ''}{formatPct(h.unrealisedReturnPct)}
                        </td>
                        {tradeMode && (
                          <td className="text-right whitespace-nowrap">
                            <div className="flex justify-end gap-2">
                              <a
                                href={`iress://ticket?symbol=${encodeURIComponent(h.code)}&action=buy`}
                                className="px-2 py-1 text-[10px] uppercase font-bold tracking-wider bg-forest/10 text-forest hover:bg-forest/20 rounded transition-colors"
                              >
                                Buy
                              </a>
                              <a
                                href={`iress://ticket?symbol=${encodeURIComponent(h.code)}&action=sell`}
                                className="px-2 py-1 text-[10px] uppercase font-bold tracking-wider bg-rust/10 text-rust hover:bg-rust/20 rounded transition-colors"
                              >
                                Sell
                              </a>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="qa-hairline-t">
                      <td colSpan={7} className="text-ink-mute">Holdings subtotal</td>
                      <td className="num">{formatAUD(a.holdings.reduce((s, h) => s + h.currentValue, 0))}</td>
                      <td></td><td></td>
                      {tradeMode && <td></td>}
                    </tr>
                    <tr>
                      <td colSpan={7} className="text-ink-mute">Cash</td>
                      <td className="num">{formatAUD(a.cashBalance)}</td>
                      <td></td><td></td>
                      {tradeMode && <td></td>}
                    </tr>
                    <tr className="font-medium">
                      <td colSpan={7}>Account value</td>
                      <td className="num">{formatAUD(a.holdings.reduce((s, h) => s + h.currentValue, 0) + a.cashBalance)}</td>
                      <td></td><td></td>
                      {tradeMode && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ExposureCard({ title, rows, kind }: { title: string; rows: ExposureRow[]; kind: 'exposure' | 'detractor' }) {
  if (rows.length === 0) {
    return (
      <div className="qa-card p-6">
        <p className="qa-eyebrow mb-3">{title}</p>
        <QAEmpty inline title={kind === 'detractor' ? 'No positions in loss.' : 'No holdings yet.'} />
      </div>
    );
  }
  return (
    <div className="qa-card p-6">
      <p className="qa-eyebrow mb-3">{title}</p>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.code} className="flex items-center justify-between gap-3 text-sm border-b border-hairline last:border-0 pb-1.5 last:pb-0">
            <span className="min-w-0">
              <span className="font-medium">{r.code}</span>
              {r.name ? <span className="text-ink-mute truncate ml-1.5">{r.name}</span> : null}
            </span>
            <span className="flex items-center gap-4 shrink-0 qa-mono text-xs">
              <span className="text-ink-mute">{formatPct(r.exposurePct)}</span>
              <span className={r.gainLoss >= 0 ? 'text-forest' : 'text-rust-deep'}>
                {r.gainLoss >= 0 ? '+' : ''}{formatAUD(r.gainLoss)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
